import React, { useEffect, useMemo, useCallback } from 'react';
import T from 'i18n';
import { FilterListOutlined, Group, Info, KeyboardArrowUp } from '@mui/icons-material';
import { Avatar, Badge, Fab, Grid, Tooltip, Typography } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useFetchMatches } from '../redux/fetchMatches';
import { useSelector } from 'react-redux';
import { selectParams } from 'common/selectors';
import SearchAnswerContainer from './SearchAnswerContainer';
import { SearchOptionsTooltip } from './SearchOptionsTooltip';
import { searchCenterLocation } from './enums';
import SearchMatchesDisplayContainer from './SearchMatchesDisplayContainer';
import { makeStyles } from '@mui/styles';
import SearchErrorPage from './SearchErrorPage';
import { useSetAISearchState } from '../redux/setAISearchState';
import DocSummary from './DocSummary';
import { removeFormatSuffix } from './utils/formatUtils';

const useStyles = makeStyles({
  answersContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '1rem 1rem 1rem 0',
  },
  answersSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  answersSectionAvatar: {
    width: 28,
    height: 28,
  },
  answersSectionHeading: {
    color: '#0000007a',
    fontWeight: 600,
  },
  scrollToTopButton: {
    position: 'absolute',
    bottom: 0,
    right: '1rem',
    zIndex: 1000,
  },
});

const blinkAnimation = `@keyframes blinker {
  50% {
    opacity: 0;
  }
}`;

const searchingTextStyle = {
  animation: `blinker 1s linear infinite`,
  fontWeight: 600,
  color: '#0000007a',
};

interface SearchContainerProps {
  selectedMatches: Array<any>;
  scrollBarRef: any;
  searchContainerId: string;
  startNewSearch: Function;
  processQuestions: (
    isSuggestedQuestions?: any,
    inpVal?: string,
    fileSidebarAskAI?: boolean,
    firstChat?: boolean,
    outputType?: string,
  ) => void;
  askAIHandler: Function;
  fileSidebarAskAI: boolean;
  viewerControl?: any;
  gridView?: boolean;
  isTranscriptPage?: boolean;
}

export default function SearchContainer({
  selectedMatches,
  scrollBarRef,
  searchContainerId,
  startNewSearch,
  processQuestions,
  askAIHandler,
  fileSidebarAskAI,
  viewerControl,
  gridView,
  isTranscriptPage,
}: SearchContainerProps) {
  const classes = useStyles();
  const { fetchDocumentsPending } = useFetchMatches();
  const { searchMatchesInput, searchAnsChat, searchSuggestedQuestions } = useSetAISearchState();
  const { file } = useSelector(selectParams);
  const [selectedContainerId, setSelectedContainerId] = React.useState<any>(null);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const matchesData = searchMatchesInput?.find((itm: any) => itm.matches) || [];
  const showMatchesResp = searchMatchesInput.some((itm: any) => itm.showMatchesResp);
  const isError = matchesData && matchesData[0]?.isError;

  // Simple fallback function for legacy compatibility
  const handleMatchesLinkClicked = (index: number, highlight: string) => {
    // This function is kept for compatibility but the actual navigation
    // is now handled directly in SearchAnswerContainer
    console.log('Legacy handleMatchesLinkClicked called', { index, highlight });
  };

  const scrollToTop = useCallback(() => {
    if (scrollBarRef.current) {
      scrollBarRef.current.scrollTop = 0;
    }
  }, [scrollBarRef]);

  // Throttle scroll events to prevent excessive re-renders
  const throttledHandleScroll = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (scrollBarRef.current) {
          const shouldShowButton = scrollBarRef.current.scrollTop > 200;
          setIsScrolled((prev) => (prev !== shouldShowButton ? shouldShowButton : prev));
        }
      }, 100); // Throttle to 100ms
    };
  }, [scrollBarRef]);

  useEffect(() => {
    const throttledHandler = throttledHandleScroll();
    const container = scrollBarRef.current;

    if (container) {
      container.addEventListener('scroll', throttledHandler, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', throttledHandler);
      }
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    const parentContainer = scrollBarRef.current;
    if (parentContainer) {
      const targetElement = document.getElementById(selectedContainerId);
      if (targetElement) {
        const targetTop = targetElement.offsetTop;
        scrollBarRef.current.scrollTop = targetTop - 200;
      }
    }
  }, [scrollBarRef, file, selectedContainerId]);

  // Memoize heavy rendering components to prevent re-renders during scroll
  const memoizedSearchMatches = useMemo(() => {
    if (isError || fileSidebarAskAI || !showMatchesResp || !searchMatchesInput?.length) {
      return null;
    }

    return searchMatchesInput.map((md: any) => (
      <SearchMatchesDisplayContainer
        key={md.id}
        matchesData={md}
        fetchDocumentsPending={fetchDocumentsPending}
        startNewSearch={startNewSearch}
        selectedMatches={selectedMatches}
        setSelectedContainerId={setSelectedContainerId}
        suggestedQuestions={searchSuggestedQuestions}
        processQuestions={processQuestions}
        askAIHandler={askAIHandler}
      />
    ));
  }, [
    isError,
    fileSidebarAskAI,
    showMatchesResp,
    searchMatchesInput,
    fetchDocumentsPending,
    startNewSearch,
    selectedMatches,
    searchSuggestedQuestions,
    processQuestions,
    askAIHandler,
  ]);

  const memoizedSearchAnswers = useMemo(() => {
    if (!searchAnsChat?.length) return null;

    return searchAnsChat.map((itm: any, index: number) => (
      <Grid item xs={12} key={itm.id || index} id={itm.id}>
        <div
          className={classes.answersContainer}
          style={{
            alignItems: itm.user === 'botIcon' ? 'start' : 'center',
          }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              itm.contributor ? (
                <Tooltip title={itm.contributor} placement="top" arrow>
                  <Avatar sx={{ width: 16, height: 16, bgcolor: 'white' }}>
                    <Group sx={{ color: 'GrayText' }} fontSize="small" />
                  </Avatar>
                </Tooltip>
              ) : null
            }
          >
            <Avatar
              className={classes.answersSectionAvatar}
              sx={{
                ...(itm.user === 'botIcon' && { bgcolor: '#85A0F1' }),
              }}
            >
              <Typography>
                {itm.user === 'botIcon' ? T.translate('case.tvAvatarText') : itm.user}
              </Typography>
            </Avatar>
          </Badge>
          <div style={{ marginLeft: '0.5rem' }}>
            <div className={classes.answersSection}>
              <Typography
                className={classes.answersSectionHeading}
                variant={itm.user === 'botIcon' || fileSidebarAskAI ? 'h6' : 'h5'}
                style={{
                  ...(fileSidebarAskAI && { fontSize: '13px' }),
                }}
              >
                {itm.user === 'botIcon'
                  ? T.translate('case.trialview')
                  : removeFormatSuffix(itm.text)}
              </Typography>
              {itm.user !== 'botIcon' && itm.options && !fileSidebarAskAI && (
                <Tooltip
                  title={<SearchOptionsTooltip options={itm.options} />}
                  placement="right"
                  arrow
                >
                  <Info fontSize="small" sx={{ color: '#85A0F1', ml: '1rem' }} />
                </Tooltip>
              )}
            </div>
            {itm.user !== 'botIcon' &&
              itm.location !== searchCenterLocation.allMatches &&
              itm.location !== 'All Matches' && (
                <div style={{ marginTop: '0.5rem' }}>
                  <FilterListOutlined fontSize="small" sx={{ mr: '0.5rem', mb: '0.2rem' }} />
                  <span>{itm.location}</span>
                </div>
              )}
            {itm.user === 'botIcon' && (
              <Typography
                variant={fileSidebarAskAI ? 'body1' : 'h6'}
                style={{
                  lineBreak: 'auto',
                  ...(fileSidebarAskAI && { fontSize: '13px' }),
                  ...(itm.text === T.translate('case.searchingText') && searchingTextStyle),
                }}
              >
                <PerfectScrollbar style={{ height: '100%' }}>
                  {itm.command === 'summarize' ? (
                    <DocSummary
                      summary={itm.text}
                      submissions={itm.submissions}
                      rulings={itm.rulings}
                      scrollBarRef={scrollBarRef}
                    />
                  ) : typeof itm.text === 'string' && !itm?.rawText ? (
                    itm.text
                  ) : (
                    <SearchAnswerContainer
                      response={itm.text}
                      rawAnswer={itm?.rawText ? itm.rawText : ''}
                      typingEffect={itm.typingEffect}
                      scrollBarRef={scrollBarRef}
                      handleMatchesLinkClicked={handleMatchesLinkClicked}
                      allMatchesCount={matchesData?.matches?.length}
                      allMatches={matchesData?.matches}
                      isTranscriptPage={isTranscriptPage}
                      fileSidebarAskAI={fileSidebarAskAI}
                      viewerControl={viewerControl}
                      gridView={gridView}
                    />
                  )}
                </PerfectScrollbar>
              </Typography>
            )}
          </div>
        </div>
      </Grid>
    ));
  }, [
    searchAnsChat,
    classes.answersContainer,
    classes.answersSectionAvatar,
    classes.answersSection,
    classes.answersSectionHeading,
    fileSidebarAskAI,
    scrollBarRef,
    matchesData?.matches,
    isTranscriptPage,
    viewerControl,
    gridView,
  ]);
  return (
    <Grid
      container
      height="100%"
      width="100%"
      sx={{
        alignContent: 'end',
        position: 'relative',
        marginTop: fileSidebarAskAI ? '-0.5rem' : '0',
        ...(fileSidebarAskAI && {
          maxHeight: 'calc(100vh - 24rem)',
          ...(gridView && { height: 'calc(100vh - 29rem)' }),
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
      id="search-container"
      aria-label={searchContainerId}
    >
      <style>{blinkAnimation}</style>
      <PerfectScrollbar
        options={{
          wheelPropagation: false, // Prevents scroll from propagating to parent
          suppressScrollX: true, // Only allow vertical scrolling
        }}
        style={{
          width: '100%',
          ...(fileSidebarAskAI && {
            flex: '1 1 auto',
            maxHeight: 'calc(100vh - 5rem)',
            overflowY: 'auto',
            padding: '0.5rem 2rem 0.5rem 0.5rem',
            minHeight: '100px',
          }),
        }}
        containerRef={(ref) => {
          scrollBarRef.current = ref;
          if (ref && fileSidebarAskAI) {
            setTimeout(() => {
              const psElement = ref as any;
              if (psElement._ps) {
                psElement._ps.update();
              }
            }, 100);
          }
        }}
      >
        {isError && (
          <SearchErrorPage errorText={matchesData?.text} startNewSearch={startNewSearch} />
        )}
        {memoizedSearchMatches}
        {memoizedSearchAnswers}
      </PerfectScrollbar>
      {isScrolled && (
        <Fab
          color="primary"
          size="small"
          variant="extended"
          className={classes.scrollToTopButton}
          onClick={scrollToTop}
          sx={{
            ...(fileSidebarAskAI && {
              bottom: '5rem',
              right: '1rem',
            }),
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      )}
    </Grid>
  );
}
